import React from 'react';
import { debounce } from '@mui/material/utils';

interface ScrollInfo {
  isScrolledPast: boolean;
  isScrollingDown: boolean;
}

export const useScrollPastElement = (
  elementRef: React.RefObject<HTMLElement | null>
) => {
  const [scrollInfo, setScrollInfo] = React.useState<ScrollInfo>({
    isScrolledPast: false,
    isScrollingDown: false,
  });

  React.useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = debounce(() => {
      const currentScrollY = window.scrollY;

      if (elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        const elementDistance = rect.top;
        const newIsScrolledPast = elementDistance < -100;
        // Read before lastScrollY moves on; the updater may run later
        const hasMoved = currentScrollY !== lastScrollY;
        const movedDown = currentScrollY > lastScrollY;

        // Only update state if values have changed
        // (no raw distance in state: it changes on every scroll and re-renders the nav)
        setScrollInfo((prevState) => {
          // Equal reads mid-scroll must not flip the direction
          const newIsScrollingDown = hasMoved
            ? movedDown
            : prevState.isScrollingDown;

          if (
            prevState.isScrolledPast !== newIsScrolledPast ||
            prevState.isScrollingDown !== newIsScrollingDown
          ) {
            return {
              isScrolledPast: newIsScrolledPast,
              isScrollingDown: newIsScrollingDown,
            };
          }
          return prevState;
        });
      }

      lastScrollY = currentScrollY;
    }, 10);

    // Initial check
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      handleScroll.clear();
    };
  }, [elementRef]);

  return scrollInfo;
};
